import { gql } from 'apollo-angular';

/**
 * Test runs for a given test suite UID — the same endpoint the Results client uses for its
 * "Test runs for test suite" page. Returns a flat list (no per-run status/sub-suite counts),
 * so consumers show it without a derived pass/fail status.
 */
export const RESULTS_TEST_RUNS_BY_TEST_SUITE = gql`
	query resultsTestRunModelsByTestSuite($uid: ID!) {
		resultsTestRunModelsByTestSuite(testSuiteUid: $uid) {
			testRun {
				testRunUid
				environment
				executionTime
				regBy
			}
			testSuite {
				testSuiteUid
				itemNo
				version
				name
			}
			testSuiteVersion
		}
	}
`;

/**
 * Test runs for a given TEST CASE UID — the endpoint behind the Results client's "results for test
 * case" page. Each run wraps the suite/spec that ran the case plus the case's result (testResults),
 * with testRunType discriminating suite (else) vs specification (410).
 */
export const RESULTS_TEST_RUNS_BY_TEST_CASE = gql`
	query resultsTestRunModelsByTestCase($uid: ID!) {
		resultsTestRunModelsByTestCase(testCaseUid: $uid) {
			testRun {
				testRunUid
				environment
				executionTime
				regBy
				testResults {
					testResultUid
					result
					resultComment
					testCaseVersion
					troubleReports {
						name
					}
				}
			}
			testSuite {
				testSuiteUid
				itemNo
				version
				name
			}
			testSpecification {
				uid
				itemNo
				version
				name
			}
			testRunType
		}
	}
`;

/**
 * Test runs for a given test specification UID — the specification counterpart to
 * RESULTS_TEST_RUNS_BY_TEST_SUITE, matching the Results client's "test specifications" page.
 * The specification query has no testSuiteVersion or parent-suite fields.
 */
export const RESULTS_TEST_RUNS_BY_TEST_SPECIFICATION = gql`
	query resultsTestRunModelsByTestSpecification($uid: ID!) {
		resultsTestRunModelsByTestSpecification(testSpecificationUid: $uid) {
			testRun {
				testRunUid
				environment
				executionTime
				regBy
			}
			testSpecification {
				uid
				itemNo
				version
				name
			}
		}
	}
`;

export const RESULTS_TEST_RUN_MODELS_BY_DATES = gql`
	query resultsTestRunModelByDates($startDate: String!, $endDate: String!) {
		resultsTestRunModelByDates(startDate: $startDate, endDate: $endDate) {
			testRun {
				testRunUid
				executionTime
				environment
				vinNo
				configuration
				sop
				testPeriod
				classification
				classificationReason
				classificationComment
				status
				regBy
				regTime
				testResults {
					testResultUid
					result
					testCaseUid
					testCaseName
					testCaseVersion
					testSuiteUid
					testSuiteName
					failedReason
					failedBy
					failedReasonComment
					resultComment
				}
			}
			testSuite {
				testSuiteUid
				itemNo
				name
				version
			}
			testSpecification {
				uid
				itemNo
				name
				version
			}
			ecuAndSwIdentifiers {
				name
				swIdentifiers
			}
			testRunType
			testSuiteVersion
		}
	}
`;

export const RESULTS_COMPLETE_TEST_RUN = gql`
	query resultsCompleteTestRun($uid: ID!) {
		resultsCompleteTestRun(uid: $uid) {
			testRun {
				testRunUid
				executionTime
				environment
				vinNo
				configuration
				sop
				testPeriod
				classification
				classificationReason
				classificationComment
				status
				regBy
				regTime
				testResults {
					testResultUid
					result
					resultComment
					inputType
					positiveComment
					negativeComment
					testCaseUid
					testCaseItemNo
					testCaseName
					testCaseVersion
					testSuiteUid
					testSuiteName
					testSuiteItemNo
					testSpecificationUid
					testSpecificationName
					testSpecificationItemNo
					failedReason
					failedReasonText
					failedBy
					failedReasonComment
					testCaseAlternativeFailedReasons {
						testCaseAlternativeFailedReason
						failedComment
						testCaseAlternativeName
					}
					troubleReports {
						name
						uri
						testResultUid
						validFrom
						validTo
					}
				}
			}
			testSuite {
				testSuiteUid
				itemNo
				name
				version
			}
			testSpecification {
				uid
				itemNo
				name
				version
			}
			testRunType
			testSuiteVersion
		}
	}
`;

export const RESULTS_TEST_SUITES_FOR_TEST_SUITE = gql`
	query resultsTestSuitesForTestSuite($uid: ID!, $version: Int) {
		resultsTestSuitesForTestSuite(uid: $uid, version: $version) {
			...testSuiteTreeFields
			testSuites {
				...testSuiteTreeFields
				testSuites {
					...testSuiteTreeFields
					testSuites {
						...testSuiteTreeFields
						testSuites {
							...testSuiteTreeFields
						}
					}
				}
			}
		}
	}
	fragment testSuiteTreeFields on ResultsTestSuite {
		testSuiteUid
		itemNo
		name
		version
		owners
		regTime
		regBy
		description
		nodeOrder
	}
`;

export const RESULTS_TEST_CASES_FOR_TEST_SUITE = gql`
	query resultsTestCasesForTestSuite($testSuiteUid: ID!, $testSuiteVersion: Int) {
		resultsTestCasesForTestSuite(testSuiteUid: $testSuiteUid, testSuiteVersion: $testSuiteVersion) {
			...testCaseTreeFields
			testCases {
				...testCaseTreeFields
				testCases {
					...testCaseTreeFields
					testCases {
						...testCaseTreeFields
						testCases {
							...testCaseTreeFields
						}
					}
				}
			}
		}
	}
	fragment testCaseTreeFields on ResultsTestCase {
		tcUid
		itemNo
		name
		version
		regBy
		regTime
		operator
		value
	}
`;

export const RESULTS_OBJECTS_UNDER_TEST_FOR_TEST_RESULT = gql`
	query resultsTestResult($uid: ID!) {
		resultsTestResult(uid: $uid) {
			testResultUid
			ecus {
				id
				name
				description
				fpcCondition
				familyId
				familyName
			}
			offboardComponents {
				uid
				value
				version
				identifier
				variant
			}
		}
	}
`;

export const RESULTS_TEST_CASE_STEPS = gql`
	query resultsTestCaseSteps($testCaseUid: ID!, $testCaseVersion: Int) {
		resultsTestCaseSteps(testCaseUid: $testCaseUid, testCaseVersion: $testCaseVersion) {
			stepNo
			precondition
			stimuli
			expectedResponse
			postcondition
		}
	}
`;

export const RESULTS_PROPERTIES_FOR_TEST_RUN = gql`
	query resultsPropertiesForTestRun($testRunUid: ID!) {
		resultsPropertiesForTestRun(testRunUid: $testRunUid) {
			name
			description
			version
			value
			valueDescription
			fieldFormat
			identifier
			variant
			label
		}
	}
`;

export const RESULTS_PROPERTIES_FOR_TEST_RESULT = gql`
	query resultsPropertiesForTestResult($testResultUid: ID) {
		resultsPropertiesForTestResult(testResultUid: $testResultUid) {
			name
			description
			version
			value
			valueDescription
			fieldFormat
			identifier
			variant
			label
		}
	}
`;

export const RESULTS_TEST_CASE_ALTERNATIVES = gql`
	query resultsTestCaseAlternatives($testCaseUid: ID!, $testCaseVersion: Int) {
		resultsTestCaseAlternatives(testCaseUid: $testCaseUid, testCaseVersion: $testCaseVersion) {
			uid
			version
			name
			label
			fromDate
			toDate
			validFromSop
			validToSop
			regBy
			regTime
			testObjects {
				uid
				name
				typeId
			}
			properties {
				name
				value
				valueDescription
				label
			}
			testGroups
		}
	}
`;

export const RESULTS_TEST_CASE_DETAILS = gql`
	query resultsTestCaseDetails($testCaseUid: ID!, $testCaseVersion: Int) {
		resultsTestCaseDetails(testCaseUid: $testCaseUid, testCaseVersion: $testCaseVersion) {
			tcUid
			itemNo
			name
			description
			version
			owners
			regBy
			regTime
			requirement
			inputTypeId
			inputTypeName
			inputTypeValues
			script
			operator
			value
			restrictedViewAccess
			versions {
				itemUid
				versionNo
				validTo
				regBy
				regTime
			}
			ufs {
				ufNumber
				name
			}
			productProperties {
				uid
				variant
				name
				productCategoryUid
				productCategoryName
			}
			ecus {
				id
				name
				description
				fpcCondition
				familyId
				familyName
			}
			offboardComponents {
				uid
				value
				version
				identifier
				variant
			}
			alternatives {
				uid
				version
				name
				label
				fromDate
				toDate
				validFromSop
				validToSop
				regBy
				regTime
				testGroups
			}
		}
	}
`;

export const RESULTS_REQUIREMENTS_WITH_TEST_ANALYSIS = gql`
	query resultsRequirementsWithTestAnalysis($testResultUid: ID) {
		resultsRequirementsWithTestAnalysis(testResultUid: $testResultUid) {
			requirement {
				requirementId
				name
				version
				asil
				irmaVersion
			}
			testAnalysis {
				uid
				itemNo
				name
				version
				regBy
				regTime
				validTo
			}
			testAnalysisAlternative {
				uid
				name
				version
				regBy
				regTime
				validTo
			}
		}
	}
`;

export const RESULTS_REQUIREMENTS_BY_TEST_CASE = gql`
	query resultsRequirementsWithTestAnalysisByTestCase($testCaseUid: ID!, $testCaseVersion: Int) {
		resultsRequirementsWithTestAnalysisByTestCase(testCaseUid: $testCaseUid, testCaseVersion: $testCaseVersion) {
			requirement {
				requirementId
				name
				version
				asil
				irmaVersion
			}
			testAnalysis {
				uid
				itemNo
				name
				version
				regBy
				regTime
				validTo
			}
			testAnalysisAlternative {
				uid
				name
				version
				regBy
				regTime
				validTo
			}
		}
	}
`;

export const RESULTS_SOPS_FOR_TEST_RUN = gql`
	query resultsSopsForTestRun($testRunUid: ID!) {
		resultsSopsForTestRun(testRunUid: $testRunUid) {
			fileUid
			fileName
			versionControlVersion
			fileTime
			importTime
			originTypeUid
			regBy
			regTime
			vinNo
			vehicleName
			fpcs {
				name
				value
				familyDescription
				variantDescription
			}
			xpcs {
				name
				value
			}
			functionParameters {
				family
				parentName
				name
				value
			}
		}
	}
`;

export const RESULTS_ECU_PARAMETERS_FOR_TEST_RUN = gql`
	query resultsEcuParametersForTestRun($testRunUid: ID!) {
		resultsEcuParametersForTestRun(testRunUid: $testRunUid) {
			typeUid
			fileUid
			name
			description
			value
			enumValue
			valueType
			ecuName
			ecuFamily
		}
	}
`;

export const RESULTS_ECU_READOUTS_FOR_TEST_RUN = gql`
	query resultsEcuReadoutsForTestRun($testRunUid: ID!) {
		resultsEcuReadoutsForTestRun(testRunUid: $testRunUid) {
			ecuName
			ecuDdb
			ecuIdentification
			name
			value
			originTypeUid
			fileTime
			testRunUid
		}
	}
`;

export const RESULTS_TEST_SUITE_ALTERNATIVES = gql`
	query resultsTestSuiteAlternativesForTestSuite($testSuiteUid: ID!, $testSuiteVersion: Int) {
		resultsTestSuiteAlternativesForTestSuite(testSuiteUid: $testSuiteUid, testSuiteVersion: $testSuiteVersion) {
			uid
			version
			name
			label
			fromDate
			toDate
			validFromSop
			validToSop
			regBy
			regTime
			properties {
				name
				value
				label
			}
			testGroups
		}
	}
`;

/** Full test suite record for the suite details page heading + Details panel (version optional → latest). */
export const RESULTS_TEST_SUITE = gql`
	query resultsTestSuite($uid: ID!, $version: Int) {
		resultsTestSuite(uid: $uid, version: $version) {
			testSuiteUid
			itemNo
			name
			version
			owners
			description
			regBy
			regTime
			ufs {
				ufNumber
				name
			}
			productProperties {
				uid
				variant
				name
				productCategoryUid
				productCategoryName
			}
			offboardComponents {
				uid
				value
				version
				identifier
				variant
			}
			versions {
				itemUid
				versionNo
				validTo
				regBy
				regTime
			}
		}
	}
`;

/** Full test case record for the test case details page (version optional → latest). */
export const RESULTS_TEST_CASE = gql`
	query resultsTestCase($uid: ID!, $version: Int) {
		resultsTestCase(testCaseUid: $uid, testCaseVersion: $version) {
			tcUid
			itemNo
			name
			description
			version
			owners
			regBy
			regTime
			requirement
			script
			steps {
				stepNo
				precondition
				stimuli
				expectedResponse
				postcondition
			}
			ufs {
				ufNumber
				name
			}
			productProperties {
				uid
				variant
				name
				productCategoryUid
				productCategoryName
			}
			ecus {
				id
				name
				description
				fpcCondition
				familyId
				familyName
			}
			offboardComponents {
				uid
				value
				version
				identifier
				variant
			}
			versions {
				itemUid
				versionNo
				validTo
				regBy
				regTime
			}
			alternatives {
				uid
				version
				name
				label
				fromDate
				toDate
				validFromSop
				validToSop
				regBy
				regTime
				testGroups
				testObjects {
					uid
					name
					typeId
				}
				properties {
					name
					value
					valueDescription
					label
				}
			}
		}
	}
`;

/** Full test specification record for the specification details page (version optional → latest). */
export const RESULTS_TEST_SPECIFICATION = gql`
	query resultsTestSpecification($uid: ID!, $version: Int) {
		resultsTestSpecification(uid: $uid, version: $version) {
			uid
			itemNo
			name
			version
			owners
			regBy
			regTime
			testAnalyses {
				uid
				itemNo
				name
				version
				regBy
				regTime
			}
			versions {
				itemUid
				versionNo
				validTo
				regBy
				regTime
			}
		}
	}
`;

/**
 * Full ECU catalogue (id/name/family) from the abstraction layer — the same endpoint the Results
 * client uses (ALL_ECUS_QUERY). Used only to resolve an `EcuForSoftwareVersion` property's numeric
 * id to a human-readable ECU name in the test-run properties panel.
 */
export const RESULTS_ALL_ECUS = gql`
	query ecus {
		ecus {
			id
			name
			description
			fpcCondition
			familyId
			familyName
		}
	}
`;
